<template>
  <SettingsCard
    :title="$t('settings.email.signature-settings.title')"
    :subtitle="$t('settings.email.signature-settings.subtitle')"
    icon="mdi-chat"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <v-row
      v-if="defaultSignatureAvailable"
      class="ma-2"
    >
      <v-col
        cols="12"
        md="6"
      >
        <div class="font-weight-bold">
          {{ $t('settings.email.signature-settings.body-texts.0') }}
        </div>
        <div class="text-grey">
          {{ $t('settings.email.signature-settings.body-texts.1') }}
          <br />
        </div>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-switch
          :model-value="localCustomTemplateSignature"
          color="primary"
          @update:model-value="handleCustomTemplateSignatureChange"
        />
      </v-col>
    </v-row>

    <!-- Default signature section -->
    <template v-if="!localCustomTemplateSignature">
      <Feature :feature-slug="featureNames.GOOGLE_REVIEW">
        <v-row class="ma-2">
          <v-col
            cols="12"
            md="6"
          >
            <div class="font-weight-bold">
              {{ $t('settings.email.signature-settings.google-review.title') }}
            </div>
            <div class="text-grey mb-4">
              {{ $t('settings.email.signature-settings.google-review.description') }}
            </div>
            <a
              class="google-review-link"
              :href="$t('settings.features.contact.google-review.link')"
              target="_blank"
              rel="noopener"
            >
              {{ $t('settings.features.contact.google-review.link') }}
            </a>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-tooltip
              location="top"
              :disabled="!!googleReviewLink"
            >
              <span>{{ $t('tooltips.google-review-not-set') }}</span>
              <template #activator="{ props }">
                <div
                  v-bind="props"
                  style="width: fit-content"
                >
                  <v-switch
                    v-model="appendGoogleReviewLink"
                    :loading="isLoading.updateIncludeGoogleReview"
                    color="primary"
                  />
                </div>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </Feature>
      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.2') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.3') }}
            <br />
            <br />
            {{ $t('settings.email.signature-settings.body-texts.4') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <signature-preview
            :company="company"
            type="html"
            :custom-signature="localSignature.html"
            :extra-signature="localSignature.extra"
            :google-review-link="googleReviewLink"
            :proven-expert-link="provenExpertLink"
          />
        </v-col>
      </v-row>

      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.5') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.6') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-textarea
            v-model="localSignature.extra"
            class="mb-4"
            hide-details
            variant="outlined"
            no-resize
            @blur="handleSignatureExtraChange(false)"
          />
          <div class="d-flex justify-end">
            <v-btn
              variant="outlined"
              :disabled="!hasSignatureExtra"
              @click="handleSignatureExtraChange(true)"
            >
              {{ $t('labels.reset') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <!-- Custom signature section -->
    <template v-else>
      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.7') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.8') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-textarea
            v-model="localSignature.html"
            class="mb-4"
            hide-details
            variant="outlined"
            no-resize
            @blur="handleSignatureHTMLChange"
          />
        </v-col>
      </v-row>

      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.11') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.12') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-textarea
            v-model="localSignature.text"
            class="mb-4"
            hide-details
            variant="outlined"
            no-resize
            @blur="handleSignatureTextChange"
          />
        </v-col>
      </v-row>

      <!-- Preview sections -->
      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.9') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.10') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <signature-preview
            :company="company"
            type="html"
            :has-custom-signature="localCustomTemplateSignature"
            :custom-signature="localSignature.html"
            :google-review-link="googleReviewLink"
            :proven-expert-link="provenExpertLink"
          />
        </v-col>
      </v-row>

      <v-row class="ma-2">
        <v-col
          cols="12"
          md="6"
        >
          <div class="font-weight-bold">
            {{ $t('settings.email.signature-settings.body-texts.13') }}
          </div>
          <div class="text-grey">
            {{ $t('settings.email.signature-settings.body-texts.14') }}
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <signature-preview
            :company="company"
            type="text"
            :has-custom-signature="localCustomTemplateSignature"
            :custom-signature="localSignature.text"
            :google-review-link="googleReviewLink"
            :proven-expert-link="provenExpertLink"
          />
        </v-col>
      </v-row>
    </template>
  </SettingsCard>
</template>

<script>
import SettingsCard from '@/components/SettingsCard.vue'
import SignaturePreview from '@/components/SignaturePreview.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import eventBus, { eventNames } from '@/lib/eventBus'
import featureMixin from '@/mixins/feature'

import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_CUSTOM_TEMPLATE_SIGNATURE from './queries/UpdateCompanyAmbassadorSignatureCustomTemplateSignature.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_EXTRA from './queries/UpdateCompanyAmbassadorSignatureExtra.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_HTML from './queries/UpdateCompanyAmbassadorSignatureHTML.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW from './queries/UpdateCompanyAmbassadorSignatureIncludeGoogleReview.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_TEXT from './queries/UpdateCompanyAmbassadorSignatureText.gql'

export default {
  emits: ['refetchCompany'],
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'signature' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: { SettingsCard, SignaturePreview },
  mixins: [featureMixin],
  props: {
    company: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    googleReviewLink: {
      type: String,
      default: null,
    },
    provenExpertLink: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isLoading: {
        updateSignatureExtra: false,
        updateSignatureText: false,
        updateSignatureHTML: false,
        updateCustomTemplateSignature: false,
        updateIncludeGoogleReview: false,
      },
      localCustomTemplateSignature: false,
      localSignature: {
        html: '',
        text: '',
        extra: '',
        includeGoogleReview: false,
      },
    }
  },

  computed: {
    appendGoogleReviewLink: {
      get() {
        return this.localSignature.includeGoogleReview
      },
      async set(value) {
        await this.handleIncludeGoogleReviewChange(value)
      },
    },
    defaultSignatureAvailable() {
      return this.company.mailProvider === 'HOPPERMATION'
    },
    hasSignatureExtra() {
      return this.localSignature.extra !== ''
    },
  },

  watch: {
    'company.ambassador.signature': {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.localCustomTemplateSignature = newValue.customTemplateSignature
          this.localSignature = {
            html: newValue.html || '',
            text: newValue.text || '',
            extra: newValue.extra || '',
            includeGoogleReview: newValue.includeGoogleReview || false,
          }
        }
      },
    },
  },

  methods: {
    async handleCustomTemplateSignatureChange(value) {
      this.isLoading.updateCustomTemplateSignature = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_CUSTOM_TEMPLATE_SIGNATURE,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              customTemplateSignature: value,
            },
          },
        })
        this.localCustomTemplateSignature = value
        this.$tracking.event(
          'Settings',
          'Clicked',
          'Update Company Ambassador signature, custom template signature',
          value ? 'Enabled' : 'Disabled'
        )
        this.showSuccessMessage()
        this.$emit('refetchCompany')
      } catch (err) {
        this.showErrorMessage()
      } finally {
        this.isLoading.updateCustomTemplateSignature = false
      }
    },

    async handleSignatureExtraChange(reset) {
      if (reset) this.localSignature.extra = ''
      this.isLoading.updateSignatureExtra = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_EXTRA,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              extra: this.localSignature.extra,
            },
          },
        })
        this.$tracking.event('Settings', 'Clicked', 'Update Company Ambassador signature EXTRA')
        this.showSuccessMessage()
        this.$emit('refetchCompany')
      } catch (err) {
        this.showErrorMessage()
      } finally {
        this.isLoading.updateSignatureExtra = false
      }
    },

    async handleIncludeGoogleReviewChange(value) {
      this.localSignature.includeGoogleReview = value
      this.isLoading.updateIncludeGoogleReview = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              includeGoogleReview: value,
            },
          },
        })
        this.$tracking.event(
          'Settings',
          'Clicked',
          'Update Company Ambassador signature include Google Review'
        )
        this.showSuccessMessage()
        this.$emit('refetchCompany')
      } catch (err) {
        this.showErrorMessage()
      } finally {
        this.isLoading.updateIncludeGoogleReview = false
      }
    },

    async handleSignatureHTMLChange() {
      this.isLoading.updateSignatureHTML = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_HTML,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              html: this.localSignature.html,
            },
          },
        })
        this.$tracking.event('Settings', 'Clicked', 'Update Company Ambassador signature HTML')
        this.showSuccessMessage()
        this.$emit('refetchCompany')
      } catch (err) {
        this.showErrorMessage()
      } finally {
        this.isLoading.updateSignatureHTML = false
      }
    },

    async handleSignatureTextChange() {
      this.isLoading.updateSignatureText = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_TEXT,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              text: this.localSignature.text,
            },
          },
        })
        this.$tracking.event('Settings', 'Clicked', 'Update Company Ambassador signature TEXT')
        this.showSuccessMessage()
        this.$emit('refetchCompany')
      } catch (err) {
        this.showErrorMessage()
      } finally {
        this.isLoading.updateSignatureText = false
      }
    },

    showSuccessMessage() {
      eventBus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'success',
        text: this.$t('alerts.settings.email.signature-settings.success'),
      })
    },

    showErrorMessage() {
      eventBus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'error',
        text: this.$t('alerts.settings.email.signature-settings.error'),
      })
    },
  },
}
</script>
<style scoped>
.google-review-link {
  color: rgb(var(--v-theme-primary));
}
</style>
